import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";

export default function Register() {
  const { register } = useAuth();
  const { showToast } = useNotifications();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setError("");
    setLoading(true);
    try {
      const user = await register(name, email, password);
      showToast("success", "Account created! Welcome to Schemackozo.");
      navigate(user.role === "admin" || user.role === "staff" ? "/admin" : "/dashboard");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-black flex font-inter">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20futuristic%20technology%20abstract%20background%20glowing%20electric%20blue%20circuit%20patterns%20deep%20space%20digital%20art%20cinematic&width=900&height=1080&seq=auth-bg-002&orientation=portrait"
          alt="Auth background"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-deep-black/20 to-deep-black/60" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <Link to="/" className="flex items-center gap-3 mb-auto mt-8">
            <img src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SCHEMACKOZO</span>
          </Link>
          <div className="mb-8 space-y-4">
            {["Track your projects in real-time", "Manage invoices and billing", "Get dedicated support"].map((f) => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-electric-blue/20 border border-electric-blue/30">
                  <i className="ri-check-line text-electric-blue text-xs" />
                </div>
                <span className="text-silver text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8 lg:hidden">
            <img src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png" alt="Logo" className="h-7 w-auto" />
            <span className="font-bold text-white text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SCHEMACKOZO</span>
          </Link>

          <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Create your account</h1>
          <p className="text-silver text-sm mb-8">Get access to your client portal.</p>

          {error && (
            <div className="flex items-center gap-2 bg-cyber-red/10 border border-cyber-red/30 rounded-xl px-4 py-3 mb-5">
              <i className="ri-error-warning-line text-cyber-red text-sm" />
              <span className="text-cyber-red text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Full Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="John Smith"
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors" />
            </div>
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com"
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors" />
            </div>
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Min. 8 characters"
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 pr-11 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-silver-dim hover:text-white cursor-pointer">
                  <i className={`${showPass ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                </button>
              </div>
              {password && (
                <div className="mt-2 flex gap-1">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${password.length >= i * 2 ? (password.length >= 8 ? "bg-cyber-green" : "bg-cyber-orange") : "bg-dark-border"}`} />
                  ))}
                </div>
              )}
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-electric-blue text-deep-black font-bold py-3.5 rounded-xl hover:shadow-glow-blue transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap cursor-pointer">
              {loading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />Creating account...</span> : "Create Account"}
            </button>
          </form>

          <p className="text-center text-silver-dim text-sm mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-electric-blue hover:underline cursor-pointer">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
